import React from 'react';

// --- 1. DÉFINITION DES DÉGRADÉS (Gradients) ---

const SparkGradient = ({ id }) => (
  <linearGradient id={id} x1="0%" y1="0%" x2="100%" y2="100%">
    <stop offset="0%" stopColor="#FF4D4D" /> {/* Rouge Vif */}
    <stop offset="100%" stopColor="#F9CB28" /> {/* Or */}
  </linearGradient>
);

const PrismGradient = ({ id }) => (
  <linearGradient id={id} x1="0%" y1="0%" x2="0%" y2="100%">
    <stop offset="0%" stopColor="#06b6d4" /> {/* Cyan */}
    <stop offset="100%" stopColor="#3b82f6" /> {/* Bleu Roi */}
  </linearGradient>
);

const FlowGradient = ({ id }) => (
  <linearGradient id={id} x1="0%" y1="0%" x2="100%" y2="0%">
    <stop offset="0%" stopColor="#8b5cf6" /> {/* Violet */}
    <stop offset="100%" stopColor="#ec4899" /> {/* Rose */}
  </linearGradient>
);

const HubGradient = ({ id }) => (
  <linearGradient id={id} x1="0%" y1="0%" x2="100%" y2="100%">
    <stop offset="0%" stopColor="#22d3ee" /> {/* Cyan Lumineux */}
    <stop offset="50%" stopColor="#3b82f6" /> {/* Bleu */}
    <stop offset="100%" stopColor="#a855f7" /> {/* Violet */}
  </linearGradient>
);

// --- 2. LES FORMES (SVG) ---

// CONCEPT 1 : THE SPARK (Énergie)
export const LogoSpark = ({ className, ...props }) => (
  <svg viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg" className={className} {...props}>
    <defs><SparkGradient id="spark-grad" /></defs>
    <path d="M12 12 H 40 V 20 H 20 V 44 H 12 V 12 Z" fill="currentColor" opacity="0.8"/>
    <path d="M52 52 H 24 V 44 H 44 V 20 H 52 V 52 Z" fill="currentColor" opacity="0.8"/>
    <path d="M32 22 L 36 32 L 46 36 L 36 40 L 32 50 L 28 40 L 18 36 L 28 32 Z" fill="url(#spark-grad)" />
  </svg>
);

// CONCEPT 2 : THE PRISM (Structure 3D)
export const LogoPrism = ({ className, ...props }) => (
  <svg viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg" className={className} {...props}>
    <defs><PrismGradient id="prism-grad" /></defs>
    <path d="M32 34 L 12 24 V 44 L 32 54 V 34 Z" fill="currentColor" className="opacity-80" />
    <path d="M32 34 L 52 24 V 44 L 32 54 V 34 Z" fill="currentColor" className="opacity-40" />
    <path d="M32 10 L 52 20 L 32 30 L 12 20 Z" fill="url(#prism-grad)" />
    <circle cx="32" cy="10" r="3" fill="url(#prism-grad)" />
  </svg>
);

// CONCEPT 3 : THE FLOW (Vitesse)
export const LogoFlow = ({ className, ...props }) => (
  <svg viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg" className={className} {...props}>
    <defs><FlowGradient id="flow-grad" /></defs>
    <path d="M10 16 H 40 C 45 16, 50 12, 54 8" stroke="currentColor" strokeWidth="6" strokeLinecap="round" />
    <path d="M10 32 H 32" stroke="url(#flow-grad)" strokeWidth="6" strokeLinecap="round" />
    <path d="M10 48 H 40 C 45 48, 50 52, 54 56" stroke="currentColor" strokeWidth="6" strokeLinecap="round" />
    <circle cx="58" cy="32" r="4" fill="url(#flow-grad)" />
  </svg>
);

// CONCEPT 4 : THE HUB (Inspiré de votre image - Centralisation)
export const LogoHub = ({ className, ...props }) => (
  <svg viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg" className={className} {...props}>
    <defs><HubGradient id="hub-grad" /></defs>
    
    {/* Anneau de connexion (les modules périphériques) */}
    <circle cx="32" cy="8" r="4" fill="currentColor" className="opacity-20" />
    <circle cx="56" cy="32" r="4" fill="currentColor" className="opacity-20" />
    <circle cx="32" cy="56" r="4" fill="currentColor" className="opacity-20" />
    <circle cx="8" cy="32" r="4" fill="currentColor" className="opacity-20" />
    
    <circle cx="49" cy="15" r="3" fill="currentColor" className="opacity-40" />
    <circle cx="49" cy="49" r="3" fill="currentColor" className="opacity-40" />
    <circle cx="15" cy="49" r="3" fill="currentColor" className="opacity-40" />
    <circle cx="15" cy="15" r="3" fill="currentColor" className="opacity-40" />
    
    {/* L'anneau central abstrait (le "E" stylisé ou lien infini) */}
    <path 
      d="M32 18 C 42 18, 46 24, 46 32 C 46 42, 38 46, 32 46 C 22 46, 18 38, 18 28 C 18 22, 22 18, 26 18" 
      stroke="url(#hub-grad)" 
      strokeWidth="6" 
      strokeLinecap="round"
    />
    
    {/* Cœur central */}
    <circle cx="32" cy="32" r="5" fill="url(#hub-grad)" />
  </svg>
);


// --- 3. CHOIX DU LOGO ACTIF ---

// Changez ici pour tester : LogoHub, LogoPrism, LogoSpark, LogoFlow
const CurrentLogoIcon = LogoHub; 

export const LogoIcon = CurrentLogoIcon;

/**
 * Composant complet (Icône + Texte)
 * S'adapte automatiquement au Dark/Light mode via Tailwind
 */
export const EventProLogo = ({ className, variant, forceWhite = false, ...props }) => {
  // Permet de forcer une variante spécifique si besoin: <EventProLogo variant="spark" />
  let IconComponent = CurrentLogoIcon;
  if (variant === 'spark') IconComponent = LogoSpark;
  if (variant === 'prism') IconComponent = LogoPrism;
  if (variant === 'flow') IconComponent = LogoFlow;
  if (variant === 'hub') IconComponent = LogoHub;

  return (
    <div className={`flex items-center gap-3 ${className}`} {...props}>
      <IconComponent className={`h-9 w-9 ${forceWhite ? 'text-white' : 'text-slate-900 dark:text-white'}`} />
      
      <div className="flex flex-col justify-center select-none">
        <span className={`text-xl font-bold leading-none tracking-tight ${forceWhite ? 'text-white' : 'text-slate-900 dark:text-white'}`}>
          Event<span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-500 to-blue-600">Pro</span>
        </span>
      </div>
    </div>
  );
};

export default EventProLogo;